import type { MinimalCard } from '@/services/cardDatabase';

// --- Draft Session ---

export type DraftStatus = 'setup' | 'loading' | 'picking' | 'between-packs' | 'review' | 'building';

export interface DraftSession {
  id: string;
  setCode: string;
  setName: string;
  bonusSheetSetCode: string | null;
  podSize: number;
  cardsPerPick: number;
  packs: DraftPack[][];
  currentPackIndex: number;
  currentPickIndex: number;
  playerPool: MinimalCard[];
  playerPicks: PickRecord[];
  aiPlayers: AIPlayer[];
  status: DraftStatus;
}

export interface DraftPack {
  cards: MinimalCard[];
  originalCards: MinimalCard[];
}

export interface PickRecord {
  packIndex: number;
  pickIndex: number;
  picked: MinimalCard[];
  availableCards: MinimalCard[];
  recommendation?: string;
}

// --- AI ---

export interface AIPlayer {
  id: number;
  pool: MinimalCard[];
  colorCommitment: ColorCommitment;
}

export interface ColorCommitment {
  primary: string | null;
  secondary: string | null;
  speculating: boolean;
}

// --- Booster Generation ---

export interface BoosterTemplate {
  commons: number;
  uncommons: number;
  rareSlots: number;
  bonusSheetSlot: boolean;
  totalCards: number;
}

export interface CardPool {
  commons: MinimalCard[];
  uncommons: MinimalCard[];
  rares: MinimalCard[];
  mythics: MinimalCard[];
}

// --- Draft Stats ---

export interface DraftStats {
  manaCurve: Record<number, number>;
  colorDistribution: Record<string, number>;
  typeBreakdown: Record<string, number>;
  creatureCount: number;
  averageCMC: number;
  totalPicks: number;
}

// --- Color Openness ---

export interface ColorOpenness {
  W: number;
  U: number;
  B: number;
  R: number;
  G: number;
}

// --- Archetypes ---

export interface SetArchetype {
  colors: [string, string];
  name: string;
  keywords: string[];
}

export interface ArchetypeScore {
  archetype: SetArchetype;
  score: number;
}

// --- Draft Config ---

export const DRAFTABLE_SET_TYPES = ['expansion', 'core', 'masters', 'draft_innovation'] as const;

export const DEFAULT_POD_SIZE = 8;
export const DEFAULT_CARDS_PER_PICK = 1;
export const PACKS_PER_DRAFT = 3;
export const MYTHIC_PROBABILITY = 1 / 7.4;
